import { db } from '../firebase';
import { collection, addDoc, getDocs, doc, getDoc, updateDoc, deleteDoc, query, where, orderBy, limit, startAfter, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { auth } from '../firebase';
import { sendOrderStatusNotification, sendOrderCancellationNotification, sendItemCancellationNotification } from './notificationApi';

const ordersCollection = collection(db, 'orders');

// Create a new order
export async function createOrder(orderData) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    // Ensure sellerId is set - if not provided, use current user (for testing scenarios)
    if (!orderData.sellerId) {
      console.warn('No sellerId provided, using current user as seller (testing mode)');
      orderData.sellerId = user.uid;
    }

    const order = {
      ...orderData,
      userId: user.uid,
      sellerId: orderData.sellerId, // Ensure this is explicitly set
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'placed',
      statusTimeline: [
        {
          key: 'placed',
          label: 'Order Placed',
          timestamp: new Date().toISOString(),
          complete: true,
          info: 'We received your order.'
        },
        {
          key: 'processing',
          label: 'Processing',
          timestamp: null,
          complete: false,
          info: 'Your order is being prepared.'
        },
        {
          key: 'out',
          label: 'Out for Delivery',
          timestamp: null,
          complete: false,
          info: 'Your order is on the way.'
        },
        {
          key: 'delivered',
          label: 'Delivered',
          timestamp: null,
          complete: false,
          info: 'Order delivered to your address.'
        }
      ]
    };

    console.log('Creating order with data:', {
      userId: order.userId,
      sellerId: order.sellerId,
      items: order.items?.length || 0
    });

    const docRef = await addDoc(ordersCollection, order);
    return { id: docRef.id, ...order };
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
}

// Fix orders missing sellerId (for existing orders)
export async function fixOrdersMissingSellerId() {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    console.log('Fixing orders missing sellerId...');
    
    // Get all orders where current user is the userId but sellerId is missing
    const ordersQuery = query(
      ordersCollection,
      where('userId', '==', user.uid),
      where('sellerId', '==', null)
    );
    
    const snapshot = await getDocs(ordersQuery);
    
    if (snapshot.empty) {
      console.log('No orders found missing sellerId');
      return { fixed: 0, total: 0 };
    }
    
    const updatePromises = snapshot.docs.map(async (doc) => {
      await updateDoc(doc.ref, {
        sellerId: user.uid,
        updatedAt: new Date().toISOString()
      });
      console.log(`Fixed order ${doc.id} - added sellerId: ${user.uid}`);
    });
    
    await Promise.all(updatePromises);
    
    console.log(`Fixed ${snapshot.docs.length} orders`);
    return { fixed: snapshot.docs.length, total: snapshot.docs.length };
  } catch (error) {
    console.error('Error fixing orders missing sellerId:', error);
    throw error;
  }
}

// Fix orders missing userId (for existing orders)
export async function fixOrdersMissingUserId() {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    console.log('Fixing orders missing userId...');
    
    // Get all orders where current user is the sellerId but userId is missing
    const ordersQuery = query(
      ordersCollection,
      where('sellerId', '==', user.uid),
      where('userId', '==', null)
    );
    
    const snapshot = await getDocs(ordersQuery);
    
    if (snapshot.empty) {
      console.log('No orders found missing userId');
      return { fixed: 0, total: 0 };
    }
    
    const updatePromises = snapshot.docs.map(async (doc) => {
      await updateDoc(doc.ref, {
        userId: user.uid, // Set userId to current user for testing scenarios
        updatedAt: new Date().toISOString()
      });
      console.log(`Fixed order ${doc.id} - added userId: ${user.uid}`);
    });
    
    await Promise.all(updatePromises);
    
    console.log(`Fixed ${snapshot.docs.length} orders`);
    return { fixed: snapshot.docs.length, total: snapshot.docs.length };
  } catch (error) {
    console.error('Error fixing orders missing userId:', error);
    throw error;
  }
}

// Fix a specific order by ID (for immediate fixes)
export async function fixOrderById(orderId, sellerId = null) {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    console.log(`Fixing order ${orderId}...`);
    
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (!orderSnap.exists()) {
      throw new Error('Order not found');
    }
    
    const orderData = orderSnap.data();
    const targetSellerId = sellerId || user.uid;
    
    // Update the order with the correct sellerId
    await updateDoc(orderRef, {
      sellerId: targetSellerId,
      updatedAt: new Date().toISOString()
    });
    
    console.log(`Fixed order ${orderId} - set sellerId to: ${targetSellerId}`);
    return { success: true, orderId, sellerId: targetSellerId };
  } catch (error) {
    console.error(`Error fixing order ${orderId}:`, error);
    throw error;
  }
}

// Get all orders for the current user
export async function getUserOrders(userId = null, limitCount = 10, lastDoc = null) {
  try {
    const user = userId || auth.currentUser?.uid;
    if (!user) throw new Error('Not authenticated');

    let q = query(
      ordersCollection,
      where("userId", "==", user),
      orderBy("createdAt", "desc"),
      limit(limitCount)
    );

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    return {
      orders,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting user orders:', error);
    throw error;
  }
}

// Get a specific order by ID
export async function getOrderById(orderId) {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (!orderSnap.exists()) {
      throw new Error('Order not found');
    }
    
    return { id: orderSnap.id, ...orderSnap.data() };
  } catch (error) {
    console.error('Error getting order by ID:', error);
    throw error;
  }
}

// Update order status
export async function updateOrderStatus(orderId, newStatus) {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (!orderSnap.exists()) {
      throw new Error('Order not found');
    }
    
    const orderData = orderSnap.data();
    
    // Validate status transition
    const validStatuses = [
      'placed', 'accepted', 'in-progress', 'ready', 'out-for-delivery', 'completed', 'canceled', 'cancelled',
      'pending', 'preparing', 'delivered' // legacy/other
    ];
    if (!validStatuses.includes(newStatus)) {
      throw new Error('Invalid status');
    }
    
    // Prepare update data
    const updateData = {
      status: newStatus,
      last_updated_at: serverTimestamp(),
      updatedAt: new Date().toISOString()
    };
    
    // Add completed_at timestamp if status is completed
    if (newStatus === 'completed') {
      updateData.completed_at = serverTimestamp();
    }
    
    // DEBUG: Log the update payload
    console.log('Order update payload:', updateData);
    
    // Update the order
    await updateDoc(orderRef, updateData);
    
    // Send notification to customer
    if (newStatus !== 'pending') { // Don't notify for initial pending status
      await sendOrderStatusNotification({ id: orderSnap.id, ...orderData }, newStatus);
    }
    
    // Return updated order data
    return { 
      id: orderSnap.id, 
      ...orderData, 
      status: newStatus,
      last_updated_at: new Date(),
      ...(newStatus === 'completed' && { completed_at: new Date() })
    };
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
}

// Update order details
export async function updateOrder(orderId, updateData) {
  try {
    const orderRef = doc(db, 'orders', orderId);
    await updateDoc(orderRef, {
      ...updateData,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating order:', error);
    throw error;
  }
}

// Cancel an order
export async function cancelOrder(orderId, reason = '') {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (!orderSnap.exists()) {
      throw new Error('Order not found');
    }
    
    const orderData = orderSnap.data();
    
    await updateDoc(orderRef, {
      status: 'cancelled',
      last_updated_at: serverTimestamp(),
      updatedAt: new Date().toISOString()
    });
    
    // Send cancellation notification
    await sendOrderCancellationNotification({ id: orderSnap.id, ...orderData }, reason);
    
  } catch (error) {
    console.error('Error cancelling order:', error);
    throw error;
  }
}

// Get orders by status
export async function getOrdersByStatus(status, userId = null) {
  try {
    const user = userId || auth.currentUser?.uid;
    if (!user) throw new Error('Not authenticated');

    const q = query(
      ordersCollection,
      where("userId", "==", user),
      where("status", "==", status),
      orderBy("createdAt", "desc")
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting orders by status:', error);
    throw error;
  }
}

// Get recent orders (last 30 days)
export async function getRecentOrders(userId = null) {
  try {
    const user = userId || auth.currentUser?.uid;
    if (!user) throw new Error('Not authenticated');

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const q = query(
      ordersCollection,
      where("userId", "==", user),
      where("createdAt", ">=", thirtyDaysAgo.toISOString()),
      orderBy("createdAt", "desc")
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting recent orders:', error);
    throw error;
  }
}

// Add a review to an order item
export async function addOrderItemReview(orderId, itemId, reviewData) {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (!orderSnap.exists()) {
      throw new Error('Order not found');
    }
    
    const orderData = orderSnap.data();
    const items = orderData.items.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          review: {
            ...reviewData,
            createdAt: new Date().toISOString()
          }
        };
      }
      return item;
    });
    
    await updateDoc(orderRef, {
      items,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error adding order item review:', error);
    throw error;
  }
}

// Get order statistics for a user
export async function getUserOrderStats(userId = null) {
  try {
    const user = userId || auth.currentUser?.uid;
    if (!user) throw new Error('Not authenticated');

    const q = query(ordersCollection, where("userId", "==", user));
    const snapshot = await getDocs(q);
    const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    const stats = {
      total: orders.length,
      confirmed: orders.filter(o => o.status === 'confirmed').length,
      processing: orders.filter(o => o.status === 'processing').length,
      delivered: orders.filter(o => o.status === 'delivered').length,
      cancelled: orders.filter(o => o.status === 'cancelled').length,
      totalSpent: orders.reduce((sum, order) => sum + ((order.total || 0) / 100), 0),
      averageOrderValue: orders.length > 0 ? orders.reduce((sum, order) => sum + ((order.total || 0) / 100), 0) / orders.length : 0
    };
    
    return stats;
  } catch (error) {
    console.error('Error getting user order stats:', error);
    throw error;
  }
} 

// Fetch all orders for a given sellerId
export async function getOrdersBySeller(sellerId) {
  try {
    if (!sellerId) throw new Error('Missing sellerId');
    const q = query(ordersCollection, where('sellerId', '==', sellerId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching orders by seller:', error);
    throw error;
  }
}

// Real-time listener for orders by seller
// @deprecated Temporarily disabled due to Firestore internal assertion errors
export function subscribeToOrdersBySeller(sellerId, callback) {
  console.warn('Real-time orders subscription temporarily disabled due to Firestore errors');
  return () => {};
}

// Real-time listener for a specific order
// @deprecated Temporarily disabled due to Firestore internal assertion errors
export function subscribeToOrder(orderId, callback) {
  console.warn('Real-time order subscription temporarily disabled due to Firestore errors');
  return () => {};
} 